/* Copy of shrd utils/enums/messenger/SendOccasion.enum.ts (keep in sync) */
import {CustomerMailTemplateTypes} from '@/enums/CustomerMailTemplateTypes.enum';
import {
    QM_FEEDBACK_WHATS_APP_TEMPLATE_TYPES,
    WhatsAppTemplateTypes,
} from '@/enums/whats-app-template-manager/WhatsAppTemplateTypes.enum';

/**
 * Absender (messenger, SenderAreaSettings): ONE default sender of the organisation (mailbox + WhatsApp number) and per send
 * occasion (Anlass) an optional own sender (mailbox and/or number differing from the default). Channel mode and standard
 * templates per occasion.
 *
 * @deprecated The fixed areas (Backoffice / QM) are gone. Kept only to read old documents (migration: Backoffice area =
 * default sender, QM area = own sender of the QM occasions where it differs) and for the legacy view (`areas`) of the
 * response that services built before the default sender still read.
 */
export enum SenderArea {
    BACKOFFICE = 'backoffice',
    QM = 'qm',
}

/** @deprecated see SenderArea */
export const SENDER_AREA_LABELS: Record<SenderArea, string> = {
    [SenderArea.BACKOFFICE]: 'Backoffice',
    [SenderArea.QM]: 'QM',
};

/** Channel per occasion; WHATSAPP_ELSE_MAIL decides per recipient (see recipientChannels) */
export enum SendChannelMode {
    WHATSAPP_ELSE_MAIL = 'whatsAppElseMail',
    MAIL = 'mail',
    WHATSAPP = 'whatsApp',
    BOTH = 'both',
}

export const SEND_CHANNEL_MODE_LABELS: Record<SendChannelMode, string> = {
    [SendChannelMode.WHATSAPP_ELSE_MAIL]: 'WhatsApp, sonst E-Mail',
    [SendChannelMode.MAIL]: 'Nur E-Mail',
    [SendChannelMode.WHATSAPP]: 'Nur WhatsApp',
    [SendChannelMode.BOTH]: 'Beides',
};

export enum SendOccasion {
    MAKEUP_PRESENCE = 'makeupPresence',
    MAKEUP_ROUND_INVITATION = 'makeupRoundInvitation',
    MAKEUP_ROUND_CONFIRMATION = 'makeupRoundConfirmation',
    COURSE_CANCELLED = 'courseCancelled',
    SUBSTITUTION_REQUEST = 'substitutionRequest',
    SUBSTITUTION_REQUEST_REOPENED = 'substitutionRequestReopened',
    /** "Vertretung bestätigt" (WhatsApp, sonst E-Mail; mail template SUBSTITUTION_CONFIRMATION) */
    SUBSTITUTION_CONFIRMATION = 'substitutionConfirmation',
    TAKEOVER_OFFER = 'takeoverOffer',
    HANDOUT_EVENT_REMINDER = 'handoutEventReminder',
    HANDOUT_EVENT_FEEDBACK_REQUEST = 'handoutEventFeedbackRequest',
    QM_FEEDBACK_CHILD = 'qmFeedbackChild',
    QM_FEEDBACK_TEACHER = 'qmFeedbackTeacher',
    QM_FEEDBACK_INSTITUTION = 'qmFeedbackInstitution',
    // --- mails sent from a dialog (mail only, no WhatsApp template yet): the sender of the occasion, else the default sender
    /** Timetable mails after "Kursplanung finalisieren" / Einschreibung / course mail (family template; institution and teachers in the dialog) */
    TIMETABLE = 'timetable',
    /** Course change notification (time, room, teacher …) to families (institution / teacher templates in the dialog) */
    COURSE_NOTIFICATION = 'courseNotification',
    COURSE_PAUSED = 'coursePaused',
    COURSE_RESUMED = 'courseResumed',
    TERMINATION_CONFIRMATION = 'terminationConfirmation',
    RETOURE = 'retoure',
    /** Free mail of the office to families (template "Allgemeine Kundenmail"), e.g. from the student / allocation view */
    COMMON_MAIL = 'commonMail',
    /** Info to the parents about a refund (text in the dialog, no template) */
    REFUND_INFO = 'refundInfo',
    MISSING_DOCUMENTS = 'missingDocuments',
    TRAINING_INVITATION = 'trainingInvitation',
    TAKEOVER_TEACHER_REPLACED = 'takeoverTeacherReplaced',
    TAKEOVER_ACCEPTANCE_REJECTED = 'takeoverAcceptanceRejected',
    /** "Übernahme bestätigen": optional confirmation to the new teacher (mail template TAKEOVER_CONFIRMED_TEACHER) */
    TAKEOVER_CONFIRMED_TEACHER = 'takeoverConfirmedTeacher',
    /** "Übernahme bestätigen": optional info to the Standort (mail template TAKEOVER_CONFIRMED_INSTITUTION) */
    TAKEOVER_CONFIRMED_INSTITUTION = 'takeoverConfirmedInstitution',
    /** "Übernahme bestätigen": optional Kursänderung to the families of the courses (mail template COURSE_NOTIFICATION_STUDENT) */
    TAKEOVER_CONFIRMED_PARENTS = 'takeoverConfirmedParents',
    HANDOUT_EVENT_CONFIRMATION = 'handoutEventConfirmation',
    HANDOUT_EVENT_FEEDBACK_INSTITUTION = 'handoutEventFeedbackInstitution',
    /** Terminwahl der Schule (Austeilrunde / Austeilaktion), mail only */
    HANDOUT_EVENT_DATE_REQUEST = 'handoutEventDateRequest',
    /**
     * Single mail of an external sales partner to a school (sales-partner, Schul-Cockpit). The partner never gets a mailbox
     * grant: sales-partner sends from the EFFECTIVE sender of this occasion (e.g. akquise@), replies come in through the
     * mail sync of that mailbox.
     */
    ACQUISITION_MAIL = 'acquisitionMail',
    // --- automatic mails (sent by Vanilla without a user, mail only): the sender of the occasion, else the default sender
    TERMINATION_ON_FULFILLMENT = 'terminationOnFulfillment',
    MISSING_DOCUMENTS_REMINDER = 'missingDocumentsReminder',
    HANDOUT_EVENT_CONFIRMATION_REMINDER = 'handoutEventConfirmationReminder',
    ACTIVITY_REGISTRATION_CONFIRMATION = 'activityRegistrationConfirmation',
    ABSENCE_EARLY_WARNING = 'absenceEarlyWarning',
    APPLICANT_DELETION = 'applicantDeletion',
    DAILY_DIGEST = 'dailyDigest',
    /** The one system mailbox: registration, password reset (user-login-handler) and the error report (messenger) */
    SYSTEM_MAIL = 'systemMail',
}

export interface SendOccasionDefinition {
    /** @deprecated Area of the old model (only to migrate old documents / the legacy view of the response) */
    legacyArea: SenderArea;
    label: string;
    /** Recipients ("An" column, the settings table groups by it) */
    to: string;
    /** Mail template type; undefined = the occasion has no mail template (WhatsApp only, or an automatic mail with fixed HTML) */
    mailTemplateType?: CustomerMailTemplateTypes;
    /** WhatsApp template type; undefined = the occasion has no WhatsApp (automatic mails) */
    whatsAppTemplateType?: WhatsAppTemplateTypes;
    /** Channel mode while nothing is stored */
    defaultMode: SendChannelMode;
    /** Automatic mail: sent by Vanilla without a user (cron, system process), no personal defaults; mail only unless it has a whatsAppTemplateType */
    automatic?: boolean;
    /**
     * Mail only from a dialog (no WhatsApp template type): channel fixed to "Nur E-Mail", personal defaults possible
     */
    mailOnly?: boolean;
    /** Mail without template (the dialog writes the text): the "Versand" line shows only the mailbox */
    mailWithoutTemplate?: boolean;
    /**
     * The occasion supports the optional "Grund für die Eltern" (placeholder publicReason): an optional second WhatsApp
     * template "mit Grund" (whatsAppTemplateWithReason) of the same type
     */
    publicReason?: boolean;
    /**
     * The dialog has a mail switch ("Bestätigung per E-Mail"): its start value is the occasion setting
     * "standardmäßig senden" (mailOnByDefault, default off)
     */
    mailSwitch?: boolean;
}

/** Fixed in code: occasion -> recipients, template types and default channel mode (order = settings table order) */
export const SEND_OCCASIONS: Record<SendOccasion, SendOccasionDefinition> = ({
    [SendOccasion.MAKEUP_PRESENCE]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Nachholtermin vor Ort', to: 'Eltern',
        mailTemplateType: CustomerMailTemplateTypes.MAKEUP_PRESENCE_DATE, whatsAppTemplateType: WhatsAppTemplateTypes.MAKEUP_PRESENCE_DATE,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL, publicReason: true,
    },
    [SendOccasion.MAKEUP_ROUND_INVITATION]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Nachholrunde – Einladung', to: 'Eltern',
        mailTemplateType: CustomerMailTemplateTypes.MAKEUP_ROUND_INVITATION, whatsAppTemplateType: WhatsAppTemplateTypes.MAKEUP_ROUND_INVITATION,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL, publicReason: true,
    },
    [SendOccasion.MAKEUP_ROUND_CONFIRMATION]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Nachholrunde – Bestätigung', to: 'Eltern',
        mailTemplateType: CustomerMailTemplateTypes.MAKEUP_ROUND_CONFIRMATION, whatsAppTemplateType: WhatsAppTemplateTypes.MAKEUP_ROUND_CONFIRMATION,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.COURSE_CANCELLED]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Kursabsage', to: 'Eltern',
        mailTemplateType: CustomerMailTemplateTypes.COURSE_CANCELLED_STUDENT, whatsAppTemplateType: WhatsAppTemplateTypes.COURSE_CANCELED,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL, publicReason: true,
    },
    [SendOccasion.SUBSTITUTION_REQUEST]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Vertretungsanfrage', to: 'Lehrkräfte',
        // the dialog asks teachers without WhatsApp by mail (as before): default = today's per-teacher choice
        mailTemplateType: CustomerMailTemplateTypes.SUBSTITUTION_REQUEST, whatsAppTemplateType: WhatsAppTemplateTypes.SUBSTITUTION_REQUEST,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.SUBSTITUTION_REQUEST_REOPENED]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Vertretung wieder frei (Warteliste)', to: 'Lehrkräfte',
        mailTemplateType: CustomerMailTemplateTypes.SUBSTITUTION_REQUEST_REOPENED, whatsAppTemplateType: WhatsAppTemplateTypes.SUBSTITUTION_REQUEST_REOPENED,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.SUBSTITUTION_CONFIRMATION]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Vertretung bestätigt', to: 'Lehrkräfte',
        mailTemplateType: CustomerMailTemplateTypes.SUBSTITUTION_CONFIRMATION, whatsAppTemplateType: WhatsAppTemplateTypes.SUBSTITUTION_CONFIRMATION,
        // stored settings keep their mode (before: WhatsApp only); new: WhatsApp, else mail
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.TAKEOVER_OFFER]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Übernahmeangebot', to: 'Lehrkräfte',
        mailTemplateType: CustomerMailTemplateTypes.TAKEOVER_OFFER, whatsAppTemplateType: WhatsAppTemplateTypes.TAKEOVER_OFFER,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.HANDOUT_EVENT_REMINDER]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Austeilaktion – Erinnerung', to: 'Werbende',
        mailTemplateType: CustomerMailTemplateTypes.HANDOUT_EVENT_REMINDER, whatsAppTemplateType: WhatsAppTemplateTypes.HANDOUT_EVENT_REMINDER,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.HANDOUT_EVENT_FEEDBACK_REQUEST]: {
        legacyArea: SenderArea.BACKOFFICE, label: 'Austeilaktion – Feedback', to: 'Werbende',
        mailTemplateType: CustomerMailTemplateTypes.HANDOUT_EVENT_FEEDBACK_REQUEST_ADVERTISER,
        whatsAppTemplateType: WhatsAppTemplateTypes.HANDOUT_EVENT_FEEDBACK_REQUEST,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.QM_FEEDBACK_CHILD]: {
        legacyArea: SenderArea.QM, label: 'Feedback-Anfrage Kinder', to: 'Eltern',
        mailTemplateType: CustomerMailTemplateTypes.QM_FEEDBACK_CHILD, whatsAppTemplateType: QM_FEEDBACK_WHATS_APP_TEMPLATE_TYPES.child,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.QM_FEEDBACK_TEACHER]: {
        legacyArea: SenderArea.QM, label: 'Feedback-Anfrage Lehrkräfte', to: 'Lehrkräfte',
        mailTemplateType: CustomerMailTemplateTypes.QM_FEEDBACK_TEACHER, whatsAppTemplateType: QM_FEEDBACK_WHATS_APP_TEMPLATE_TYPES.teacher,
        defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
    },
    [SendOccasion.QM_FEEDBACK_INSTITUTION]: {
        legacyArea: SenderArea.QM, label: 'Feedback-Anfrage Standorte', to: 'Standorte',
        mailTemplateType: CustomerMailTemplateTypes.QM_FEEDBACK_INSTITUTION, whatsAppTemplateType: QM_FEEDBACK_WHATS_APP_TEMPLATE_TYPES.institution,
        // Standorte have no mobile number: by mail unless chosen otherwise
        defaultMode: SendChannelMode.MAIL,
    },
} as Record<SendOccasion, SendOccasionDefinition>);

/** Mails sent from a dialog without WhatsApp: mail only, the dialog shows the "Versand" line of the occasion */
const mailOccasion = (label: string, to: string, mailTemplateType?: CustomerMailTemplateTypes): SendOccasionDefinition => ({
    legacyArea: SenderArea.BACKOFFICE, label, to, mailTemplateType, defaultMode: SendChannelMode.MAIL, mailOnly: true,
});

SEND_OCCASIONS[SendOccasion.TIMETABLE] = mailOccasion('Stundenplan nach Einteilung', 'Eltern', CustomerMailTemplateTypes.TIMETABLE_STUDENT);
SEND_OCCASIONS[SendOccasion.COURSE_NOTIFICATION] = mailOccasion('Kursänderung', 'Eltern', CustomerMailTemplateTypes.COURSE_NOTIFICATION_STUDENT);
SEND_OCCASIONS[SendOccasion.COURSE_PAUSED] = mailOccasion('Kurspause', 'Eltern', CustomerMailTemplateTypes.COURSE_PAUSED);
SEND_OCCASIONS[SendOccasion.COURSE_RESUMED] = mailOccasion('Kurs geht weiter', 'Eltern', CustomerMailTemplateTypes.COURSE_RESUMED);
SEND_OCCASIONS[SendOccasion.COMMON_MAIL] = mailOccasion('Mail an Familien (freie Vorlage)', 'Eltern', CustomerMailTemplateTypes.COMMON_CUSTOMER_MAIL);
SEND_OCCASIONS[SendOccasion.REFUND_INFO] = {...mailOccasion('Erstattung – Info an Eltern', 'Eltern'), mailWithoutTemplate: true};
SEND_OCCASIONS[SendOccasion.TERMINATION_CONFIRMATION] = {...mailOccasion('Kündigungsbestätigung', 'Kund:innen', CustomerMailTemplateTypes.TERMINATION), mailSwitch: true};
SEND_OCCASIONS[SendOccasion.RETOURE] = mailOccasion('Retourenschein', 'Kund:innen', CustomerMailTemplateTypes.RETOURE_MAIL);
SEND_OCCASIONS[SendOccasion.MISSING_DOCUMENTS] = mailOccasion('Fehlende Dokumente – Erinnerung', 'Lehrkräfte', CustomerMailTemplateTypes.MISSING_DOCUMENTS_TEACHER);
SEND_OCCASIONS[SendOccasion.TRAINING_INVITATION] = mailOccasion('Schulung – Einladung', 'Lehrkräfte', CustomerMailTemplateTypes.TRAINING_INVITATION);
SEND_OCCASIONS[SendOccasion.TAKEOVER_TEACHER_REPLACED] = mailOccasion('Übernahme – Lehrkraft ersetzt', 'Lehrkräfte', CustomerMailTemplateTypes.TAKEOVER_TEACHER_REPLACED);
SEND_OCCASIONS[SendOccasion.TAKEOVER_ACCEPTANCE_REJECTED] = mailOccasion('Übernahme – Zusage abgelehnt', 'Lehrkräfte', CustomerMailTemplateTypes.TAKEOVER_ACCEPTANCE_REJECTED);
// "Übernahme bestätigen" – three optional notifications, each switch starts with "standardmäßig senden" (default off)
SEND_OCCASIONS[SendOccasion.TAKEOVER_CONFIRMED_TEACHER] = {
    ...mailOccasion('Übernahme bestätigt – neue Lehrkraft', 'Lehrkräfte', CustomerMailTemplateTypes.TAKEOVER_CONFIRMED_TEACHER), mailSwitch: true,
};
SEND_OCCASIONS[SendOccasion.TAKEOVER_CONFIRMED_INSTITUTION] = {
    ...mailOccasion('Übernahme bestätigt – Standort', 'Standorte', CustomerMailTemplateTypes.TAKEOVER_CONFIRMED_INSTITUTION), mailSwitch: true,
};
SEND_OCCASIONS[SendOccasion.TAKEOVER_CONFIRMED_PARENTS] = {
    ...mailOccasion('Übernahme bestätigt – Eltern (Kursänderung)', 'Eltern', CustomerMailTemplateTypes.COURSE_NOTIFICATION_STUDENT), mailSwitch: true,
};
/** Terminbestätigung aus dem Dialog: WhatsApp, sonst E-Mail (Standorte nur per Mail – der Dialog erzwingt dort E-Mail) */
SEND_OCCASIONS[SendOccasion.HANDOUT_EVENT_CONFIRMATION] = {
    legacyArea: SenderArea.BACKOFFICE, label: 'Austeilaktion – Terminbestätigung', to: 'Werbende',
    mailTemplateType: CustomerMailTemplateTypes.HANDOUT_EVENT_CONFIRMATION,
    whatsAppTemplateType: WhatsAppTemplateTypes.HANDOUT_EVENT_CONFIRMATION_REQUEST,
    defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
};
SEND_OCCASIONS[SendOccasion.HANDOUT_EVENT_FEEDBACK_INSTITUTION] = mailOccasion('Austeilaktion – Feedback', 'Standorte', CustomerMailTemplateTypes.HANDOUT_EVENT_FEEDBACK_REQUEST_INSTITUTION);
SEND_OCCASIONS[SendOccasion.HANDOUT_EVENT_DATE_REQUEST] = mailOccasion('Austeilaktion – Terminwahl Schule', 'Standorte', CustomerMailTemplateTypes.HANDOUT_EVENT_DATE_REQUEST);
SEND_OCCASIONS[SendOccasion.ACQUISITION_MAIL] = mailOccasion('Akquise-Mail (Salespartner)', 'Schulen', CustomerMailTemplateTypes.ACQUISITION_MAIL);

/** Automatic mails: recipients and template types; the labels tell the office what sends from the chosen mailbox */
const automaticOccasion = (label: string, to: string, mailTemplateType?: CustomerMailTemplateTypes): SendOccasionDefinition => ({
    legacyArea: SenderArea.BACKOFFICE, label, to, mailTemplateType, defaultMode: SendChannelMode.MAIL, automatic: true,
});

SEND_OCCASIONS[SendOccasion.TERMINATION_ON_FULFILLMENT] = automaticOccasion('Kündigungsbestätigung bei Zustellung (automatisch)', 'Kund:innen', CustomerMailTemplateTypes.TERMINATION);
SEND_OCCASIONS[SendOccasion.MISSING_DOCUMENTS_REMINDER] = automaticOccasion('Fehlende Dokumente – Erinnerung (automatisch)', 'Lehrkräfte', CustomerMailTemplateTypes.MISSING_DOCUMENTS_TEACHER);
// automatisch, aber mit WhatsApp: „Terminbestätigung (Anfrage)“ an Austeilende/Lehrkräfte, sonst Mail (Standorte immer Mail)
SEND_OCCASIONS[SendOccasion.HANDOUT_EVENT_CONFIRMATION_REMINDER] = {
    ...automaticOccasion('Austeilaktion – Terminbestätigung (automatisch)', 'Werbende', CustomerMailTemplateTypes.HANDOUT_EVENT_CONFIRMATION),
    whatsAppTemplateType: WhatsAppTemplateTypes.HANDOUT_EVENT_CONFIRMATION_REQUEST,
    defaultMode: SendChannelMode.WHATSAPP_ELSE_MAIL,
};
SEND_OCCASIONS[SendOccasion.ACTIVITY_REGISTRATION_CONFIRMATION] = automaticOccasion('Anmeldebestätigung (automatisch)', 'Eltern');
SEND_OCCASIONS[SendOccasion.ABSENCE_EARLY_WARNING] = automaticOccasion('Abwesenheits-Frühwarnung (automatisch)', 'Eltern', CustomerMailTemplateTypes.ABSENCE_EARLY_WARNING);
SEND_OCCASIONS[SendOccasion.APPLICANT_DELETION] = automaticOccasion('Löschantrag – Bestätigung (automatisch)', 'Bewerber:innen');
SEND_OCCASIONS[SendOccasion.DAILY_DIGEST] = automaticOccasion('Tages-Digest der Benachrichtigungen (automatisch)', 'Mitarbeitende');
SEND_OCCASIONS[SendOccasion.SYSTEM_MAIL] = automaticOccasion('Systemmails: Registrierung, Passwort zurücksetzen, Fehlerbericht (automatisch)', 'System');

/**
 * Occasions a person can set personal defaults for / send from a dialog (automatic mails have no user). Not the
 * acquisition mail: the sales-partner always sends it from the sender of the occasion (no personal default).
 */
export const DIALOG_OCCASIONS = (Object.keys(SEND_OCCASIONS) as SendOccasion[])
    .filter((o) => !SEND_OCCASIONS[o].automatic && o !== SendOccasion.ACQUISITION_MAIL);

/** QM feedback target group -> occasion */
export const QM_FEEDBACK_OCCASIONS: Record<'child' | 'teacher' | 'institution', SendOccasion> = {
    child: SendOccasion.QM_FEEDBACK_CHILD,
    teacher: SendOccasion.QM_FEEDBACK_TEACHER,
    institution: SendOccasion.QM_FEEDBACK_INSTITUTION,
};
